import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { BreadcrumbJsonLd, FaqJsonLd } from '../components/StructuredData';
import PublicHeader from '../components/PublicHeader';
import { appLinks } from '../lib/crossSiteLinks';

const landingFaqs = [
  {
    question: 'Does EvidentTrail require write access to our repositories?',
    answer: 'No. EvidentTrail uses read-only GitHub App access and never modifies code, settings, or repository configuration.',
  },
  {
    question: 'What evidence does it collect?',
    answer: 'Two types: (1) Automated GitHub metadata — pull request approvals, reviewer identities, CI check results, branch protection settings, drift events, and exception records, captured continuously with no source code stored. (2) Manual uploads — you can upload files, screenshots, and documents as evidence items; each is SHA-256 checksum-verified and linked to a specific control and framework.',
  },
  {
    question: 'Does this certify SOC 2 or ISO 27001 compliance?',
    answer: 'No. EvidentTrail generates evidence packs that support your audit review. Certification requires a licensed CPA firm (SOC 2) or accredited body (ISO 27001).',
  },
  {
    question: 'Is there a free option?',
    answer: 'Yes. Guest mode provides a 24-hour session for PR approvals reports and branch protection snapshots — no account required.',
  },
  {
    question: 'How long does setup take?',
    answer: 'Most teams connect GitHub and see their first evidence pack within 10 minutes. There is nothing to install and no config files to write.',
  },
  {
    question: 'What frameworks does it support?',
    answer: 'SOC 2 (CC8.1, CC6.1), ISO 27001 (A.8.32, A.8.9), and EU AI Act (Article 14 human oversight). Evidence is mapped to specific control requirements.',
  },
];

const c = {
  container: 'max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8',
  section: 'py-20 md:py-28',
  sectionAlt: 'py-20 md:py-28 bg-slate-800/30',
  card: 'bg-slate-800/50 border border-slate-700/50 rounded-xl p-6',
  h2: 'text-2xl sm:text-3xl font-bold text-white',
  h3: 'text-base font-semibold text-white',
  body: 'text-slate-400',
  small: 'text-sm text-slate-500',
};

const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <SEO
        title="GitHub Evidence for SOC 2, ISO 27001 & EU AI Act | EvidentTrail"
        description="Capture GitHub PR approvals, branch protection drift, and AI-assisted commits as continuous, tamper-evident evidence — mapped to SOC 2 CC8.1, ISO 27001 A.8.32, and EU AI Act requirements."
      />
      <BreadcrumbJsonLd items={[{ name: 'Home', url: 'https://evidenttrail.com/' }]} />
      <FaqJsonLd items={landingFaqs} />
      <PublicHeader />

      <main>

        {/* ================================================================ */}
        {/* HERO                                                             */}
        {/* ================================================================ */}
        <section className="pt-24 pb-16 md:pt-36 md:pb-20">
          <div className={`${c.container} text-center`}>

            <div className="inline-flex items-center gap-2 bg-amber-900/30 border border-amber-500/30 text-amber-400 text-xs font-medium px-3 py-1.5 rounded-full mb-8">
              <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
              EU AI Act enforcement: August 2, 2026 — {Math.ceil((new Date('2026-08-02').getTime() - Date.now()) / 86400000)} days away
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 max-w-3xl mx-auto">
              Stop preparing for audits.{' '}
              <span className="text-primary-400">Start passing them.</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
              EvidentTrail captures GitHub PR approvals, branch protection drift, CI results, and AI oversight metadata as continuous, tamper-evident packs —
              ready for SOC 2, ISO 27001, and EU AI Act auditors.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <a href={appLinks.register} className="btn btn-primary text-base px-8 py-3.5 w-full sm:w-auto shadow-lg shadow-primary-500/20">
                Start Capturing Evidence Free
              </a>
              <Link to="/tools" className="btn btn-ghost text-base px-8 py-3.5 w-full sm:w-auto border border-slate-700 hover:border-slate-500 transition-colors">
                Try Free Tools — No Signup →
              </Link>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckIcon /> Read-only GitHub access</span>
              <span className="flex items-center gap-1.5"><CheckIcon /> No source code stored</span>
              <span className="flex items-center gap-1.5"><CheckIcon /> SHA-256 tamper-evident exports</span>
              <span className="flex items-center gap-1.5"><CheckIcon /> Setup in under 10 minutes</span>
            </div>

          </div>
        </section>

        {/* ================================================================ */}
        {/* TRUST BAR                                                        */}
        {/* ================================================================ */}
        <section className="border-y border-slate-800/60 bg-slate-900/40 py-8">
          <div className={c.container}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { stat: '4 frameworks', label: 'SOC 2 · ISO 27001 · EU AI Act · SDLC' },
                { stat: 'SHA-256', label: 'Tamper-evident checksums on every pack' },
                { stat: '< 10 min', label: 'Median time from install to first evidence pack' },
                { stat: 'Read-only', label: 'Zero write permissions to your repositories' },
              ].map(({ stat, label }) => (
                <div key={stat}>
                  <div className="text-xl font-bold text-white mb-1">{stat}</div>
                  <div className="text-xs text-slate-500">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* PAIN SECTION                                                     */}
        {/* ================================================================ */}
        <section className={c.section}>
          <div className={c.container}>
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className={`${c.h2} mb-4`}>The audit prep problem no one talks about</h2>
                <p className={`${c.body} max-w-2xl mx-auto`}>
                  Most teams already use GitHub. The evidence is there. The problem is getting it out in the right format before auditors ask — without two weeks of spreadsheet work.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  {
                    icon: '🔥',
                    title: 'Last-minute audit scramble',
                    reality: 'Teams manually export GitHub data, take screenshots, and build spreadsheets — then auditors question whether the evidence is complete.',
                  },
                  {
                    icon: '⚠️',
                    title: 'Branch protection weakened undetected',
                    reality: 'No alert, no record, no before/after snapshot. The change happened weeks before the audit with no evidence it was addressed.',
                  },
                  {
                    icon: '🤖',
                    title: 'AI tooling now under audit scope',
                    reality: 'EU AI Act and SOC 2 reviewers are asking who reviewed AI-assisted code changes. Most teams have no structured answer.',
                  },
                ].map(({ icon, title, reality }) => (
                  <div key={title} className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-6">
                    <div className="text-2xl mb-3">{icon}</div>
                    <p className="text-slate-200 font-semibold text-sm mb-2">{title}</p>
                    <p className="text-slate-500 text-sm">{reality}</p>
                  </div>
                ))}
              </div>
              <p className="text-center text-slate-400 mt-8 text-sm">
                EvidentTrail captures this evidence continuously — so when the audit comes, it's already done.
              </p>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* WHAT AUDITORS ASK FOR                                           */}
        {/* ================================================================ */}
        <section className={c.sectionAlt}>
          <div className={c.container}>
            <div className="text-center mb-12">
              <h2 className={c.h2}>What auditors ask for. GitHub already has it.</h2>
              <p className={`${c.body} mt-3 max-w-xl mx-auto`}>
                EvidentTrail extracts the evidence that's already in your GitHub workflow — no new tools, no new processes for your engineering team.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                {
                  ask: 'Was every change reviewed?',
                  has: 'Pull request approval events',
                  captures: 'Reviewer identity, approval timestamp, and status — linked to each merged PR.',
                  link: '/soc2-change-management-evidence',
                  label: 'SOC 2 CC8.1 evidence →',
                },
                {
                  ask: 'Were branch controls enforced?',
                  has: 'Branch protection rules and change history',
                  captures: 'Configuration snapshots before/after each change — drift events with severity classification.',
                  link: '/branch-protection-drift-detection',
                  label: 'Drift detection →',
                },
                {
                  ask: 'Did tests pass before deploy?',
                  has: 'Required CI status check results',
                  captures: 'Check name, pass/fail result, and timestamp — linked to each pull request.',
                  link: '/ci-evidence',
                  label: 'CI evidence →',
                },
              ].map(({ ask, has, captures, link, label }) => (
                <div key={ask} className={c.card}>
                  <p className="text-slate-500 text-xs uppercase tracking-wider mb-3">Auditors ask</p>
                  <h3 className="text-white font-semibold text-base mb-4">{ask}</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">GitHub has</p>
                      <p className="text-slate-300 text-sm mt-1">{has}</p>
                    </div>
                    <div className="border-t border-slate-700 pt-3">
                      <p className="text-xs text-primary-400 uppercase tracking-wider font-medium">We capture</p>
                      <p className="text-slate-400 text-sm mt-1">{captures}</p>
                    </div>
                  </div>
                  <Link to={link} className="mt-4 text-xs text-primary-400 hover:underline block">{label}</Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* HOW IT WORKS                                                     */}
        {/* ================================================================ */}
        <section id="how-it-works" className={`${c.section} scroll-mt-20`}>
          <div className={c.container}>
            <div className="max-w-2xl mx-auto">
              <h2 className={`${c.h2} mb-3`}>Up and running in minutes</h2>
              <p className={`${c.body} mb-12`}>No config files. No agents to deploy. No changes to your engineering workflow.</p>
              <div className="space-y-10">
                {[
                  {
                    n: '1',
                    title: 'Install the read-only GitHub App',
                    desc: 'Select the repositories you want to track. No write permissions — the App never modifies code, settings, or branches. Takes about two minutes.',
                  },
                  {
                    n: '2',
                    title: 'Evidence is captured automatically — and manually',
                    desc: 'PR approvals, CI check results, branch protection snapshots, drift events, and exception records are captured as they happen. You can also manually upload files and screenshots as evidence items — each checksum-verified and linked to a specific control.',
                  },
                  {
                    n: '3',
                    title: 'Export packs when auditors ask',
                    desc: 'Download PDF, CSV, JSON, and manifest files with SHA-256 checksums. Send the pack to your auditor the same day they ask.',
                  },
                ].map(({ n, title, desc }) => (
                  <div key={n} className="flex items-start gap-5">
                    <div className="w-9 h-9 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm mt-0.5">
                      {n}
                    </div>
                    <div>
                      <h3 className={`${c.h3} mb-1.5`}>{title}</h3>
                      <p className={c.body}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                  Connect GitHub Free
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* WHAT YOU GET                                                     */}
        {/* ================================================================ */}
        <section className={c.sectionAlt}>
          <div className={c.container}>
            <div className="text-center mb-12">
              <h2 className={c.h2}>Five evidence types. One compliance record.</h2>
              <p className={`${c.body} mt-3 max-w-xl mx-auto`}>Each type maps to specific control requirements so you can hand auditors exactly what they need.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'PR Approvals & CI',
                  badge: 'SOC 2 CC8.1 · ISO A.8.32',
                  desc: 'Reviewer identity, approval timestamps, and required check results — linked per pull request.',
                  link: '/github-change-control',
                },
                {
                  title: 'Drift Detection',
                  badge: 'SOC 2 CC6.1 · ISO A.8.9',
                  desc: 'Branch protection changes captured with before/after snapshots and severity classification. Alerts when controls are weakened.',
                  link: '/branch-protection-drift-detection',
                },
                {
                  title: 'Exception Records',
                  badge: 'SOC 2 CC7.4 · ISO A.8.32',
                  desc: 'Emergency merges and policy bypasses documented with justification, approver, and remediation trail.',
                  link: '/exception-tracking',
                },
                {
                  title: 'AI Oversight',
                  badge: 'EU AI Act Art. 14',
                  desc: 'GitHub Copilot co-author signals linked to human reviewer approvals. No prompts or code content stored.',
                  link: '/github-copilot-compliance',
                },
                {
                  title: 'Manual Evidence Upload',
                  badge: 'SOC 2 · ISO 27001',
                  desc: 'Upload screenshots, documents, and files as evidence items. SHA-256 checksum-verified, linked to a control, and reviewed with a full approval audit trail.',
                  link: '/features',
                },
              ].map(({ title, badge, desc, link }) => (
                <Link key={title} to={link} className={`${c.card} hover:border-primary-500/50 transition-colors block group`}>
                  <span className="inline-block text-xs text-primary-400 bg-primary-900/30 border border-primary-700/30 rounded px-2 py-0.5 mb-3 font-mono">{badge}</span>
                  <h3 className={`${c.h3} mb-2 group-hover:text-primary-400 transition-colors`}>{title}</h3>
                  <p className={`${c.body} text-sm`}>{desc}</p>
                  <span className="mt-4 text-primary-400 text-xs font-medium block">Learn more →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* FREE TOOLS                                                       */}
        {/* ================================================================ */}
        <section className={c.section}>
          <div className={c.container}>
            <div className="max-w-3xl mx-auto">
              <div className="bg-primary-900/20 border border-primary-500/20 rounded-2xl p-8 md:p-10">
                <div className="inline-block text-xs bg-primary-600/20 border border-primary-500/30 text-primary-300 rounded-full px-3 py-1 mb-4 font-medium">
                  Free — no account needed
                </div>
                <h2 className={`${c.h2} mb-3`}>Try it before you commit to anything</h2>
                <p className={`${c.body} mb-8`}>
                  Three free tools available in guest mode — no account, no GitHub write access.
                  Generate a report for any repository in under two minutes.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    { label: 'PR Approvals Report', desc: 'Export reviewer identities and approval timestamps', to: '/tools/pr-approvals-report' },
                    { label: 'Branch Protection Snapshot', desc: 'Export current protection rules as an evidence pack', to: '/tools/branch-protection-snapshot' },
                    { label: 'EU AI Act Risk Classifier', desc: 'Classify your AI system and get a compliance checklist', to: '/tools/ai-risk-classifier' },
                  ].map(({ label, desc, to }) => (
                    <Link key={to} to={to} className={`${c.card} hover:border-primary-500/50 transition-colors group`}>
                      <p className="text-white font-medium text-sm group-hover:text-primary-400 transition-colors">{label}</p>
                      <p className="text-slate-500 text-xs mt-1">{desc}</p>
                      <span className="text-primary-400 text-xs block mt-3">Free, no signup →</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* SECURITY                                                         */}
        {/* ================================================================ */}
        <section id="security" className={`${c.sectionAlt} scroll-mt-20`}>
          <div className={c.container}>
            <div className="max-w-2xl mx-auto">
              <h2 className={`${c.h2} mb-3`}>Built for security-conscious teams</h2>
              <p className={`${c.body} mb-10`}>Every decision we've made about permissions, data storage, and export format is designed to hold up under auditor scrutiny.</p>
              <div className="space-y-6">
                {[
                  {
                    title: 'Read-only GitHub App',
                    desc: 'Permissions are scoped to metadata reads only. The App never writes to repositories, changes settings, or modifies branch protection.',
                  },
                  {
                    title: 'Metadata-only capture',
                    desc: 'No source code, commit messages, review comments, AI prompts, or generated content is stored — only structured event metadata.',
                  },
                  {
                    title: 'SHA-256 tamper-evident exports',
                    desc: 'Every evidence pack includes a manifest file with checksums so auditors can verify integrity independently.',
                  },
                  {
                    title: 'Multi-tenant isolation with audit logging',
                    desc: 'Evidence data is isolated per organization. All access and export events are logged for your own audit trail.',
                  },
                ].map(({ title, desc }) => (
                  <div key={title} className="flex items-start gap-4">
                    <CheckCircleIcon />
                    <div>
                      <p className="text-white font-medium text-sm">{title}</p>
                      <p className={`${c.body} text-sm mt-0.5`}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm">
                <Link to="/security" className="text-primary-400 hover:underline">Full security details</Link>
                {' · '}
                <Link to="/read-only-github-audit" className="text-primary-400 hover:underline">Read-only access explained</Link>
              </p>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* FAQ                                                              */}
        {/* ================================================================ */}
        <section id="faq" className={`${c.section} scroll-mt-20`}>
          <div className={c.container}>
            <div className="max-w-2xl mx-auto">
              <h2 className={`${c.h2} mb-10`}>Frequently asked questions</h2>
              <div className="space-y-8">
                {landingFaqs.map(({ question, answer }) => (
                  <div key={question} className="border-b border-slate-700/50 pb-8 last:border-0 last:pb-0">
                    <h3 className="text-white font-semibold mb-2">{question}</h3>
                    <p className={c.body}>{answer}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm">
                <Link to="/faq" className="text-primary-400 hover:underline">More questions →</Link>
              </p>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* FINAL CTA — URGENCY                                              */}
        {/* ================================================================ */}
        <section className={`${c.sectionAlt} pb-32`}>
          <div className={`${c.container} text-center`}>
            <div className="max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-amber-900/30 border border-amber-500/30 text-amber-400 text-xs font-medium px-3 py-1.5 rounded-full mb-6">
                <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
                EU AI Act enforcement deadline: August 2, 2026
              </div>
              <h2 className={`${c.h2} mb-4`}>Your next audit is closer than it feels.</h2>
              <p className={`${c.body} mb-8 max-w-md mx-auto`}>
                Teams that start capturing evidence now have months of continuous change control records when the request arrives. Teams that don't start today spend those weeks in spreadsheets.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
                <a href={appLinks.register} className="btn btn-primary text-base px-8 py-3.5 shadow-lg shadow-primary-500/20">
                  Start Capturing Evidence Free
                </a>
                <Link to="/tools" className="text-slate-300 hover:text-white text-sm transition-colors">
                  Try free tools first →
                </Link>
              </div>
              <p className={`${c.small}`}>No credit card required · Free tools available without an account</p>
            </div>
          </div>
        </section>

      </main>

      {/* ================================================================== */}
      {/* FOOTER                                                             */}
      {/* ================================================================== */}
      <footer className="border-t border-slate-800/50 py-16">
        <div className={c.container}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-white font-semibold">EvidentTrail</span>
              </div>
              <p className="text-slate-500 text-sm">
                GitHub change control evidence for SOC 2, ISO 27001, and EU AI Act compliance.
              </p>
            </div>

            {/* Compliance */}
            <div>
              <h4 className="text-slate-300 font-medium text-sm mb-3">Compliance</h4>
              <ul className="space-y-2">
                {[
                  { label: 'SOC 2 Change Management', to: '/soc2-change-management-evidence' },
                  { label: 'SOC 2 GitHub Guide', to: '/soc2-github-compliance' },
                  { label: 'ISO 27001 GitHub Guide', to: '/iso-27001-github-compliance' },
                  { label: 'ISO 27001 A.8.32', to: '/solutions/iso-27001-a832-evidence' },
                  { label: 'EU AI Act GitHub Guide', to: '/eu-ai-act-github-compliance' },
                ].map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} className="text-slate-500 hover:text-slate-300 text-sm transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Use Cases */}
            <div>
              <h4 className="text-slate-300 font-medium text-sm mb-3">Use Cases</h4>
              <ul className="space-y-2">
                {[
                  { label: 'GitHub Copilot Compliance', to: '/github-copilot-compliance' },
                  { label: 'AI-Assisted Dev Evidence', to: '/ai-assisted-development-evidence' },
                  { label: 'Branch Protection Drift', to: '/branch-protection-drift-detection' },
                  { label: 'Exception Tracking', to: '/exception-tracking' },
                  { label: 'Audit Readiness', to: '/audit-readiness' },
                ].map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} className="text-slate-500 hover:text-slate-300 text-sm transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-slate-300 font-medium text-sm mb-3">Product</h4>
              <ul className="space-y-2">
                {[
                  { label: 'Free Tools', to: '/tools' },
                  { label: 'PR Approvals Report', to: '/tools/pr-approvals-report' },
                  { label: 'Branch Protection Snapshot', to: '/tools/branch-protection-snapshot' },
                  { label: 'AI Risk Classifier', to: '/tools/ai-risk-classifier' },
                  { label: 'Solutions Hub', to: '/solutions' },
                ].map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} className="text-slate-500 hover:text-slate-300 text-sm transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-slate-300 font-medium text-sm mb-3">Company</h4>
              <ul className="space-y-2">
                {[
                  { label: 'FAQ', to: '/faq' },
                  { label: 'Security', to: '/security' },
                  { label: 'Privacy Policy', to: '/privacy' },
                  { label: 'Terms of Service', to: '/terms' },
                ].map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} className="text-slate-500 hover:text-slate-300 text-sm transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800/50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
              <p>&copy; {new Date().getFullYear()} EvidentTrail. All rights reserved. Operated by EvidentTrail (Early Access), Netherlands.</p>
              <p>We respect your privacy. Your data is used only to provide the service.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
